import "./Login.scss";

import { EyeIcon, EyeOff } from "lucide-react";
import { LoginResponse, loginService } from "../../services/auth.service";
import React, { useEffect, useState } from "react";

import logo from "../../logo192.png";
import showToast from "../../utils/show.toast";
import { useNavigate } from "react-router-dom";

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const token = localStorage.getItem("token");
  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      localStorage.setItem("user_email", email);
      const request = await loginService(email, password);
      const data: LoginResponse = await request.json();
      if (!data.result) {
        showToast(`${data.message}`, false);
        return;
      }

      if (data.result.requires2FA) {
        localStorage.setItem("loginSessionId", data.result.loginSessionId);
        showToast("Login successful. Account verification required.", true);
        navigate("/otp-verification");
      }
    } catch (err) {
      showToast(
        `${
          err instanceof Error
            ? err.message
            : "Something went wrong. Please try again later."
        }`,
        false,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 rounded-lg">
      <div className="w-full max-w-md bg-white shadow-lg rounded-lg p-8">
        <div className="flex flex-col items-center justify-center mb-6">
          <div className="mb-4 ml-[1.5rem]">
            <img src={logo} alt="Brand Logo" className="h-20 w-20" />
          </div>
          <h1 className="text-2xl font-bold text-brand">Admin Login</h1>
          <p className="text-gray-600 text-[15px] mt-2 break-words">
            Enter your email and password to access dashboard
          </p>
        </div>

        <form className="space-y-5 py-5" onSubmit={handleSubmit}>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-brand focus:outline-none"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-2 px-4 bg-brand text-white mt-3 font-semibold rounded-md shadow hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
          >
            {loading ? "Logging..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
